import { useState, useRef } from 'react';
import { api } from './api';

export default function ImportUpwork({ projects, onClose, onComplete }) {
  const [step, setStep] = useState(1);      // 1 = upload, 2 = map, 3 = review, 4 = done
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const fileInputRef = useRef(null);

  // Step 1 → 2
  const [fileName,    setFileName]    = useState('');
  const [parsed,      setParsed]      = useState(null);  // { unique_clients, all_projects, rows, row_count }
  const [mappings,    setMappings]    = useState({});    // client_name → { project_id|null, new_project_name, rate }
  const [flagPlatform, setFlagPlatform] = useState(true);

  // Step 4
  const [result, setResult] = useState(null);

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setLoading(true); setError('');
    try {
      const text = await file.text();
      const data = await api.importUpworkParse(text);
      setParsed(data);
      // Build default mappings
      const m = {};
      data.unique_clients.forEach(c => {
        m[c.client_name] = {
          project_id:       c.existing_project_id ? String(c.existing_project_id) : '',
          new_project_name: c.suggested_new_name,
          rate:             c.rate,
        };
      });
      setMappings(m);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function updateMapping(client_name, field, value) {
    setMappings(prev => ({
      ...prev,
      [client_name]: { ...prev[client_name], [field]: value },
    }));
  }

  async function handleCommit() {
    setLoading(true); setError('');
    try {
      const mapPayload = parsed.unique_clients.map(c => {
        const m = mappings[c.client_name];
        return {
          client_name:      c.client_name,
          project_id:       m.project_id ? parseInt(m.project_id) : null,
          new_project_name: m.new_project_name,
          rate:             c.rate,
        };
      });
      const res = await api.importUpworkCommit({
        mappings:              mapPayload,
        rows:                  parsed.rows,
        flag_as_platform_work: flagPlatform,
      });
      setResult(res);
      setStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function finishAndClose() {
    onComplete && onComplete();
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="modal-title">Import Upwork CSV</div>
            {fileName && (
              <div className="modal-sub">
                {fileName}
                {parsed && ` · ${parsed.row_count} entries · ${parsed.unique_clients.length} unique client${parsed.unique_clients.length === 1 ? '' : 's'}`}
              </div>
            )}
          </div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>

        {/* Step nav */}
        {step < 4 && (
          <div className="step-nav">
            <div className={`step ${step === 1 ? 'active' : step > 1 ? 'done' : ''}`}>1 · Upload</div>
            <div className={`step ${step === 2 ? 'active' : step > 2 ? 'done' : ''}`}>2 · Map clients</div>
            <div className={`step ${step === 3 ? 'active' : step > 3 ? 'done' : ''}`}>3 · Review &amp; confirm</div>
          </div>
        )}

        {error && <p className="error-msg">{error}</p>}

        {/* ─── Step 1: Upload ─── */}
        {step === 1 && (
          <div style={{ padding: '1.5rem 0', textAlign: 'center' }}>
            <p style={{ color: '#737370', marginBottom: 16, fontSize: 13 }}>
              Upload your Upwork CSV export. The file should include columns:
              <br />
              <span style={{ fontSize: 11, fontFamily: 'SFMono-Regular, monospace' }}>
                Client Name · Work Date · Hours · Rate · Memo · Activity
              </span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              style={{ display: 'none' }}
            />
            <button className="btn-primary" onClick={() => fileInputRef.current?.click()} disabled={loading}>
              {loading ? 'Parsing…' : '↑ Choose CSV file'}
            </button>
          </div>
        )}

        {/* ─── Step 2: Map ─── */}
        {step === 2 && parsed && (
          <div>
            <p style={{ fontSize: 12, color: '#737370', marginBottom: 10 }}>
              Match each Upwork client to an existing project, or create a new one.
            </p>

            <div className="map-header">
              <div>Upwork client</div>
              <div />
              <div>Your project</div>
              <div>Rate</div>
            </div>

            {parsed.unique_clients.map(c => {
              const m = mappings[c.client_name];
              return (
                <div className="map-row" key={c.client_name}>
                  <div>
                    <strong>{c.client_name}</strong>
                    <div style={{ fontSize: 11, color: '#a8a8a3' }}>
                      {c.contract_id && `Contract ${c.contract_id} · `}
                      {c.entry_count} entr{c.entry_count === 1 ? 'y' : 'ies'} · {c.total_hours.toFixed(1)} hrs
                    </div>
                  </div>
                  <div className="arrow">→</div>
                  <div>
                    <select
                      value={m.project_id}
                      onChange={e => updateMapping(c.client_name, 'project_id', e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="">＋ Create new "{c.suggested_new_name}"</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    {!m.project_id && (
                      <input
                        value={m.new_project_name}
                        onChange={e => updateMapping(c.client_name, 'new_project_name', e.target.value)}
                        style={{ width: '100%', marginTop: 5, fontSize: 12 }}
                        placeholder="New project name"
                      />
                    )}
                  </div>
                  <div style={{ fontSize: 13 }}>${Number(c.rate).toFixed(0)}/hr</div>
                </div>
              );
            })}

            <div className="modal-footer">
              <label className="checkbox-wrap" style={{ fontSize: 12, color: '#737370' }}>
                <input type="checkbox" checked={flagPlatform} onChange={e => setFlagPlatform(e.target.checked)} />
                <span>Flag new projects as <strong style={{ color: '#1a1a1a' }}>platform work</strong> (no invoice number required)</span>
              </label>
              <button className="btn-primary" onClick={() => setStep(3)} disabled={loading}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 3: Review ─── */}
        {step === 3 && parsed && (
          <div>
            <p style={{ fontSize: 12, color: '#737370', marginBottom: 10 }}>
              About to insert <strong>{parsed.row_count}</strong> time entries. Review below:
            </p>
            <div className="table-wrap" style={{ maxHeight: 280, overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Hours</th>
                    <th>Rate</th>
                    <th>Memo</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 12 }}>{r.work_date}</td>
                      <td style={{ fontSize: 13 }}>{r.client_name}</td>
                      <td style={{ fontSize: 13, textAlign: 'right' }}>{r.hours.toFixed(1)}</td>
                      <td style={{ fontSize: 13 }}>${r.rate.toFixed(0)}</td>
                      <td className="desc-cell" style={{ fontSize: 12 }}>{r.memo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn-outline" onClick={() => setStep(2)}>← Back</button>
              <button className="btn-primary" onClick={handleCommit} disabled={loading}>
                {loading ? 'Importing…' : 'Import now'}
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 4: Done ─── */}
        {step === 4 && result && (
          <div style={{ padding: '1.5rem 0', textAlign: 'center' }}>
            <div style={{ fontSize: 40, color: '#16a34a', marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 12 }}>Import complete</div>
            <div style={{ fontSize: 13, color: '#737370', lineHeight: 1.7 }}>
              {result.inserted_entries} entr{result.inserted_entries === 1 ? 'y' : 'ies'} inserted
              {result.created_projects.length > 0 &&
                ` · ${result.created_projects.length} new project${result.created_projects.length === 1 ? '' : 's'} created`}
              {result.skipped_entries.length > 0 &&
                ` · ${result.skipped_entries.length} skipped`}
            </div>
            <button className="btn-primary" style={{ marginTop: 20 }} onClick={finishAndClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
